import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import "./ProposalReport.css";

export default function CustomerQuote() {
    const { partId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [terms, setTerms] = useState({
        validity_days: 30,
        payment_terms: "30 days from invoice",
        delivery_weeks: "8-10 weeks ARO",
        tax_note: "GST extra as applicable",
        ref_number: "",
    });

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/parts/${partId}/report`);
                setData(r.data);
                // try restore saved terms from part.notes JSON tail (lightweight)
                const p = r.data.part;
                if (p?.customer_quote_terms) setTerms(p.customer_quote_terms);
            } catch { toast.error("Couldn't load part"); }
        })();
    }, [partId]);

    const saveTerms = async () => {
        try {
            await api.put(`/parts/${partId}`, { ...data.part, customer_quote_terms: terms });
            toast.success("Quote terms saved");
        } catch { toast.error("Couldn't save terms"); }
    };

    if (!data) return <div className="p-8 text-zinc-500 text-sm">Loading customer quote…</div>;

    const { part, totals } = data;
    const pricePerPc = Number(totals.final_cost || 0);
    const annualVol = Number(part.annual_volume || 0);
    const projectYears = Number(part.project_years || 5);
    const annualValue = pricePerPc * annualVol;
    const today = new Date();
    const validUntil = new Date(today.getTime() + (Number(terms.validity_days) || 30) * 86400000);
    const rs = (x) => `₹ ${Number(x || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const ref = terms.ref_number || `MSC/CQ/${String(today.getFullYear()).slice(-2)}/${String(today.getMonth() + 1).padStart(2, "0")}/${(part.part_number || part.part_id).slice(-4).toUpperCase()}`;

    return (
        <div>
            {/* Toolbar (hidden in print) */}
            <div className="no-print sticky top-0 z-10 bg-white border-b border-zinc-200 px-6 py-3 flex items-center gap-3">
                <Button variant="outline" className="rounded-none" onClick={() => navigate(`/parts/${partId}/costing`)} data-testid="back-cq">
                    <ArrowLeft size={14} className="mr-2" /> Back
                </Button>
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Customer Quotation · {part.part_name}</div>
                <div className="flex-1" />
                <Button variant="outline" className="rounded-none" onClick={saveTerms} data-testid="cq-save-terms">Save terms</Button>
                <Button variant="outline" className="rounded-none" onClick={() => window.open(`${API}/parts/${partId}/customer-quote/export?validity_days=${terms.validity_days}&payment_terms=${encodeURIComponent(terms.payment_terms)}&delivery_weeks=${encodeURIComponent(terms.delivery_weeks)}&tax_note=${encodeURIComponent(terms.tax_note)}&ref_number=${encodeURIComponent(ref)}`, "_blank")} data-testid="cq-export-excel">
                    <Download size={14} className="mr-2" /> Excel
                </Button>
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227B] text-white" onClick={() => window.print()} data-testid="cq-print">
                    <Printer size={14} className="mr-2" /> Download PDF / Print
                </Button>
            </div>

            {/* Editable terms (hidden in print) */}
            <div className="no-print bg-zinc-50 border-b border-zinc-200 px-6 py-3 grid grid-cols-5 gap-3 text-sm" data-testid="cq-terms">
                <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Quotation Ref</Label>
                    <Input value={terms.ref_number} onChange={(e) => setTerms({ ...terms, ref_number: e.target.value })} placeholder={ref} className="rounded-none h-8 font-mono" data-testid="cq-ref" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Validity (days)</Label>
                    <Input type="number" value={terms.validity_days} onChange={(e) => setTerms({ ...terms, validity_days: e.target.value })} className="rounded-none h-8 font-mono" data-testid="cq-validity" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Payment Terms</Label>
                    <Input value={terms.payment_terms} onChange={(e) => setTerms({ ...terms, payment_terms: e.target.value })} className="rounded-none h-8" data-testid="cq-payment" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Delivery</Label>
                    <Input value={terms.delivery_weeks} onChange={(e) => setTerms({ ...terms, delivery_weeks: e.target.value })} className="rounded-none h-8" data-testid="cq-delivery" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Tax Note</Label>
                    <Input value={terms.tax_note} onChange={(e) => setTerms({ ...terms, tax_note: e.target.value })} className="rounded-none h-8" data-testid="cq-tax" />
                </div>
            </div>

            {/* A4 sheet — CUSTOMER VERSION (no internal cost breakdown) */}
            <div className="bg-zinc-100 py-6 print:py-0 print:bg-white">
                <div className="report-sheet" data-testid="customer-quote-sheet">
                    <div className="r-stripe" />

                    {/* HEADER */}
                    <div className="flex items-start justify-between mb-4">
                        <img src="/macho-logo.png" alt="MACHO" style={{ height: "16mm" }} />
                        <div className="text-center">
                            <div className="r-h1">Quotation</div>
                            <div className="r-meta justify-center">
                                <span>Ref · {ref}</span>
                                <span>Valid until · {validUntil.toLocaleDateString("en-IN")}</span>
                            </div>
                        </div>
                        <div className="r-meta flex-col items-end" style={{ textAlign: "right" }}>
                            <div>Date: {today.toLocaleDateString("en-IN")}</div>
                            <div>Macho Engineering Systems</div>
                            <div>Bangalore, India</div>
                        </div>
                    </div>

                    {/* TO / FROM */}
                    <div className="grid grid-cols-2 gap-6 my-4">
                        <div>
                            <div className="r-h3">To</div>
                            <div className="text-sm font-medium">{part.customer_name || "—"}</div>
                            <div className="text-xs text-zinc-500">{part.segment}</div>
                        </div>
                        <div>
                            <div className="r-h3">Part</div>
                            <div className="text-sm font-medium">{part.part_name}</div>
                            {part.part_number && <div className="text-xs font-mono text-zinc-600">P/N: {part.part_number}</div>}
                        </div>
                    </div>

                    {/* PRICE TABLE — single line item, NO cost breakdown */}
                    <div className="r-h2 mt-4" data-testid="cq-price-section">Commercial Offer</div>
                    <table className="w-full text-sm border border-zinc-300" style={{ borderCollapse: "collapse" }}>
                        <thead style={{ backgroundColor: "#002FA7", color: "white" }}>
                            <tr>
                                <th className="text-left p-2" style={{ border: "1px solid #002FA7" }}>Item</th>
                                <th className="text-right p-2" style={{ border: "1px solid #002FA7" }}>Annual Volume</th>
                                <th className="text-right p-2" style={{ border: "1px solid #002FA7" }}>Unit Price (₹)</th>
                                <th className="text-right p-2" style={{ border: "1px solid #002FA7" }}>Annual Value (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="p-2" style={{ border: "1px solid #d4d4d8" }}>
                                    <div className="font-medium">{part.part_name}</div>
                                    {part.part_number && <div className="text-[10px] font-mono text-zinc-500">P/N: {part.part_number}</div>}
                                </td>
                                <td className="text-right p-2 font-mono" style={{ border: "1px solid #d4d4d8" }} data-testid="cq-annual-vol">{annualVol.toLocaleString("en-IN")}</td>
                                <td className="text-right p-2 font-mono font-semibold" style={{ border: "1px solid #d4d4d8" }} data-testid="cq-unit-price">{rs(pricePerPc)}</td>
                                <td className="text-right p-2 font-mono font-semibold" style={{ border: "1px solid #d4d4d8" }} data-testid="cq-annual-value">{rs(annualValue)}</td>
                            </tr>
                            <tr style={{ backgroundColor: "#f4f4f5" }}>
                                <td colSpan={2} className="p-2 text-right text-xs uppercase tracking-wider text-zinc-600" style={{ border: "1px solid #d4d4d8" }}>Project life value ({projectYears} years)</td>
                                <td className="p-2" style={{ border: "1px solid #d4d4d8" }} />
                                <td className="text-right p-2 font-mono font-bold" style={{ border: "1px solid #d4d4d8" }} data-testid="cq-lifetime-value">{rs(annualValue * projectYears)}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* TERMS — customer-facing only */}
                    <div className="r-h2 mt-5">Commercial Terms</div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm" data-testid="cq-terms-block">
                        <div><span className="text-zinc-500 text-[10px] uppercase tracking-wider mr-2">Validity</span>{terms.validity_days} days from quote date</div>
                        <div><span className="text-zinc-500 text-[10px] uppercase tracking-wider mr-2">Payment</span>{terms.payment_terms}</div>
                        <div><span className="text-zinc-500 text-[10px] uppercase tracking-wider mr-2">Delivery</span>{terms.delivery_weeks}</div>
                        <div><span className="text-zinc-500 text-[10px] uppercase tracking-wider mr-2">Taxes</span>{terms.tax_note}</div>
                        <div><span className="text-zinc-500 text-[10px] uppercase tracking-wider mr-2">Pricing basis</span>FOB Bangalore, INR ex-works</div>
                        <div><span className="text-zinc-500 text-[10px] uppercase tracking-wider mr-2">Currency</span>Indian Rupee (₹)</div>
                    </div>

                    {/* SIGN-OFF */}
                    <div className="grid grid-cols-2 gap-12 mt-12">
                        <div>
                            <div className="border-t border-zinc-400 pt-2 text-xs">For Macho Engineering Systems</div>
                            <div className="text-[10px] text-zinc-500 mt-1">Authorised signatory</div>
                        </div>
                        <div>
                            <div className="border-t border-zinc-400 pt-2 text-xs">For {part.customer_name || "Customer"}</div>
                            <div className="text-[10px] text-zinc-500 mt-1">Accepted &amp; Confirmed</div>
                        </div>
                    </div>

                    <div className="mt-6 text-[9px] text-zinc-400 italic text-center">
                        This is a commercial quotation. Cost structure and process details are proprietary and not disclosed.
                    </div>
                </div>
            </div>
        </div>
    );
}
